import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  computed,
  signal,
  inject,
  input,
  viewChild,
  output,
  TemplateRef,
  OnInit,
  ElementRef,
  DestroyRef,
  afterNextRender,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  CalendarEvent,
  CalendarView,
  CalendarMonthViewComponent,
  CalendarWeekViewComponent,
  CalendarDayViewComponent,
  CalendarDatePipe,
  DateAdapter,
  provideCalendar,
  CalendarMonthViewBeforeRenderEvent,
  CalendarWeekViewBeforeRenderEvent,
  CalendarDayViewBeforeRenderEvent,
} from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { CalendarToolbarComponent } from './components/calendar-toolbar/calendar-toolbar.component';
import { CalendarHelperService } from './services/calendar-helper.service';
import { CalendarSegmentBgPipe } from './pipes/calendar-segment-bg.pipe';
import {
  BusinessCalendarEvent,
  WorkingDaysResponse,
} from '../../../core/common/models/calendar.model';
import { ReportService } from '../../../core/common/services/report/report.service';
import { MonthCellTemplateComponent } from './components/month-cell-template/month-cell-template.component';
import {
  CalendarFilledIconComponent,
  RepeatIconComponent,
} from '@kuiper/icons';
import { CalendarEventModalComponent } from '../../../core/common/components/calendar-event-modal/calendar-event-modal.component';
import { CalendarHelper } from '../../../core/common/helpers/calendar/calendar.helper';
import { Language } from '../../../core/common/models/language.model';
import { UserDropdownViewComponent } from '../user-dropdown-view/user-dropdown-view.component';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NgTemplateOutlet } from '@angular/common';
import {
  CALENDAR_EVENT_COLOR,
  CALENDAR_EVENT_SOURCE_TYPE,
} from '../../../core/common/constants/calendar-event-color';
import { CalendarEventNeighborsDirective } from '../../../core/common/directives/calendar-event-neighbor/calendar-event-neighbors.directive';

@Component({
  selector: 'lib-calendar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    provideCalendar({
      provide: DateAdapter,
      useFactory: adapterFactory,
    }),
  ],
  imports: [
    FormsModule,
    CalendarMonthViewComponent,
    CalendarWeekViewComponent,
    CalendarDayViewComponent,
    CalendarDatePipe,
    CalendarToolbarComponent,
    CalendarSegmentBgPipe,
    MonthCellTemplateComponent,
    CalendarFilledIconComponent,
    UserDropdownViewComponent,
    TranslocoPipe,
    NgTemplateOutlet,
    CalendarEventModalComponent,
    RepeatIconComponent,
    CalendarEventNeighborsDirective,
  ],
  templateUrl: './calendar.component.html',
  styleUrl: './calendar.component.scss',
})
export class CalendarComponent implements OnInit {
  readonly CalendarView = CalendarView;
  readonly CALENDAR_EVENT_COLOR = CALENDAR_EVENT_COLOR;
  readonly CALENDAR_EVENT_SOURCE_TYPE = CALENDAR_EVENT_SOURCE_TYPE;

  readonly calendarHelperService = inject(CalendarHelperService);
  readonly reportService = inject(ReportService);
  readonly transloco = inject(TranslocoService);
  readonly calendarHelper = inject(CalendarHelper);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  readonly monthCellTemplateComponent = viewChild(MonthCellTemplateComponent);
  readonly customMonthCellTemplate = computed(
    () => this.monthCellTemplateComponent()?.customMonthCellTemplate() ?? null
  );

  workingDays = input.required<WorkingDaysResponse | null>();
  defaultView = input<CalendarView>(CalendarView.Week);
  initialViewDate = input<Date>(new Date());
  calendarEvents = input<BusinessCalendarEvent[]>([]);
  segmentHeight = input<number>(this.reportService.getCurrentValueInPixels(5)); // height in pixels for each hour segment
  eventActionsTemplate = input<TemplateRef<unknown> | null>(null);
  weekStartsOn = computed<number>(() =>
    this.calendarHelperService.getWeekStartIndex(
      this.workingDays()?.start_day.value || 'sunday'
    )
  ); // specific day (e.g. 1 for Monday)

  eventClick = output<CalendarEvent>();

  view = signal<CalendarView>(CalendarView.Week);
  viewDate = signal(new Date());
  activeDayIsOpen = signal(false);
  lang = signal<Language>(this.transloco.getActiveLang() as Language);

  private readonly viewPeriod = signal<{ start: Date; end: Date } | null>(null);
  private hasScrolledToFirstBgWhite = signal<boolean>(false);
  readonly scrollToSegment = afterNextRender(() => {
    if (this.workingDays()) {
      this.scrollToFirstBgWhite();
    }
  });

  ngOnInit(): void {
    this.view.set(this.defaultView());
    this.viewDate.set(this.initialViewDate());
  }

  private scrollToFirstBgWhite(): void {
    if (this.hasScrolledToFirstBgWhite()) {
      return;
    }

    const container = this.elementRef.nativeElement.querySelector(
      '.cal-container'
    ) as HTMLElement | null;
    const target = container?.querySelector('.bg-white') as HTMLElement | null;

    if (!target) {
      return;
    }

    this.hasScrolledToFirstBgWhite.set(true);
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  events = computed<CalendarEvent[]>(() => {
    const businessEvents = this.calendarEvents();
    const period = this.viewPeriod();

    if (!period) {
      return businessEvents
        .filter(event => !event.recurrence)
        .map(event => this.calendarHelperService.mapToCalendarEvent(event));
    }

    return this.calendarHelperService.expandEventsForPeriod(
      businessEvents,
      period.start,
      period.end
    );
  });

  selectedEvent = computed<BusinessCalendarEvent | null>(() =>
    this.calendarHelper.selectedEvent()
  );

  closeEventDetails(): void {
    this.calendarHelper.clearSelectedEvent();
  }

  isSelectedOccurrence(event: CalendarEvent): boolean {
    return this.calendarHelper.isSelectedOccurrence(event);
  }

  setView(view: CalendarView): void {
    this.view.set(view);
  }

  handleChangeViewDate(date: Date): void {
    this.viewDate.set(date);
  }

  closeOpenMonthViewDay(): void {
    this.activeDayIsOpen.set(false);
  }

  beforeViewRender(
    viewRender:
      | CalendarMonthViewBeforeRenderEvent
      | CalendarWeekViewBeforeRenderEvent
      | CalendarDayViewBeforeRenderEvent
  ): void {
    const { start, end } = viewRender.period;
    const currentPeriod = this.viewPeriod();

    if (
      currentPeriod &&
      currentPeriod.start.getTime() === start.getTime() &&
      currentPeriod.end.getTime() === end.getTime()
    ) {
      return;
    }

    this.viewPeriod.set({ start, end });
    this.cdr.detectChanges();
    this.scrollToFirstBgWhite();
  }
}
